import { apiClient } from './api'
import type { ApiResponse } from '@/types/common'
import type { Lesson, CreateLessonDTO, UpdateLessonDTO } from '@/types/lesson'

export interface PendingReviewFilters {
  moduleId?: string
  limit?: number
  offset?: number
}

export const lessonService = {
  getByModuleId: async (moduleId: string): Promise<ApiResponse<Lesson[]>> => {
    return apiClient.get<Lesson[]>(`/lessons?module_id=${moduleId}`)
  },

  getById: async (id: string): Promise<ApiResponse<Lesson>> => {
    return apiClient.get<Lesson>(`/lessons/${id}`)
  },

  getAll: async (): Promise<ApiResponse<Lesson[]>> => {
    return apiClient.get<Lesson[]>('/lessons')
  },

  create: async (data: CreateLessonDTO): Promise<ApiResponse<Lesson>> => {
    return apiClient.post<Lesson>('/lessons', data)
  },

  update: async (id: string, data: UpdateLessonDTO): Promise<ApiResponse<Lesson>> => {
    return apiClient.put<Lesson>(`/lessons/${id}`, data)
  },

  delete: async (id: string): Promise<ApiResponse<void>> => {
    return apiClient.delete<void>(`/lessons/${id}`)
  },

  setPreviewable: async (id: string, isPreviewable: boolean): Promise<ApiResponse<void>> => {
    return apiClient.put<void>(`/lessons/${id}/previewable`, { is_previewable: isPreviewable })
  },

  reorder: async (lessonIds: string[]): Promise<ApiResponse<void>> => {
    return apiClient.post<void>('/lessons/reorder', { lessonIds })
  },

  getContentUrl: async (lessonId: string): Promise<ApiResponse<{ url: string; content_type: string; expires_in: number }>> => {
    return apiClient.get<{ url: string; content_type: string; expires_in: number }>(`/lessons/${lessonId}/content-url`)
  },

  // ---------------------------------------------------------------------------
  // M3 Content Authoring Workflow
  // ---------------------------------------------------------------------------

  submitForReview: async (id: string): Promise<ApiResponse<Lesson>> => {
    return apiClient.post<Lesson>(`/lessons/${id}/submit-for-review`, {})
  },

  requestDelete: async (id: string): Promise<ApiResponse<Lesson>> => {
    return apiClient.post<Lesson>(`/lessons/${id}/request-delete`, {})
  },

  approveLesson: async (id: string): Promise<ApiResponse<Lesson>> => {
    return apiClient.post<Lesson>(`/admin/lessons/${id}/approve`, {})
  },

  rejectLesson: async (id: string, note: string): Promise<ApiResponse<Lesson>> => {
    return apiClient.post<Lesson>(`/admin/lessons/${id}/reject`, { note })
  },

  approveDelete: async (id: string): Promise<ApiResponse<void>> => {
    return apiClient.post<void>(`/admin/lessons/${id}/approve-delete`, {})
  },

  getPendingReview: async (filters: PendingReviewFilters = {}): Promise<ApiResponse<Lesson[]>> => {
    const params = new URLSearchParams()
    if (filters.moduleId) params.set('module_id', filters.moduleId)
    if (filters.limit !== undefined) params.set('limit', String(filters.limit))
    if (filters.offset !== undefined) params.set('offset', String(filters.offset))
    const suffix = params.toString() ? `?${params.toString()}` : ''
    return apiClient.get<Lesson[]>(`/admin/lessons/pending-review${suffix}`)
  },
}
